import { createSlice } from '@reduxjs/toolkit'
import {
  FcErrorCorrectionLevel,
  FcGeneratorOptions,
  FcShape,
  FcText
} from '@dtx-company/flowcode-generator-browser'
import { getSquareShapeOptions } from '@dtx-company/app/shared/code/src/containers/create-flowcode-fullpage/utils/getSquareShapeOptions'
import { getFrameOptions } from '@dtx-company/app/shared/code/src/components/EditFlowcodeDesignV2/utils/getFrameOptions'
import { createFrameContainerText } from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/GeneratorSections/CodeDesign/FrameAndText/utils/createContainerText'
import {
  DEFAULT_LOCG_CIRCLE_RIM_TEXT_FONTSIZE,
  DEFAULT_LOCG_SQUARE_RIM_TEXT_FONTSIZE
} from '@dtx-company/app/shared/code/src/components/LoggedOutCodeGeneratorV2/LoggedOutCodeGenerator.constants'
import { isContrastRatioScannable } from '@dtx-company/app/shared/common/src/utils/colors'
import { isPredefinedColorChipColor } from '@dtx-company/app/shared/code/src/utils/flowcode/isPredefinedColorChipColor'
import { removeEyeBrandingFromConfig } from '@dtx-company/app/shared/code/src/containers/create-flowcode-fullpage/utils/removeEyeBrandingFromConfig'
import { addEyeBrandingToConfig } from '@dtx-company/app/shared/code/src/containers/create-flowcode-fullpage/utils/addEyeBrandingToConfig'
import { TV_CODE_FLOWCODE_OPTIONS } from '@dtx-company/app/shared/code/src/utils/create-flowcode'
import { WritableDraft } from 'immer/dist/types/types-external'
import { SINCLIAR_ORG_CODE_PRIVACY_TEXT } from '@dtx-company/app/shared/code/src/containers/create-flowcode-fullpage/constants'
import {
  CreateFlowcodeState,
  defaultAutoDesign,
  DesignType,
  DestinationType,
  initialState,
  placeholderCustomDesign,
  Social
} from './constants'

const colorPositionElement = <T extends FcGeneratorOptions['positionElementBottomLeft']>(
  element: T,
  dataPatternColor: string,
  backgroundColor: string
): T =>
  ({
    ...element,
    innerShapeColor: dataPatternColor,
    outerShapeColor: dataPatternColor,
    ...(element?.innerShapeOutlineColor && {
      innerShapeOutlineColor: dataPatternColor
    }),
    ...(element?.outerShapeOutlineColor && {
      outerShapeOutlineColor: dataPatternColor
    }),
    ...(element?.text && {
      text: {
        ...element.text,
        fontColor: dataPatternColor
      }
    }),
    backgroundColor
  }) as T

const writeVideoOptimizedStyles = (state: WritableDraft<CreateFlowcodeState>) => {
  state.activeConfig = { ...state.activeConfig, ...TV_CODE_FLOWCODE_OPTIONS }

  // cannot use video optimized styles and rim text at the same time
  state.activeConfig.containerRimText = state.defaultActiveConfig.containerRimText
  state.activeConfig.containerRimWidth = state.defaultActiveConfig.containerRimWidth
  state.selectedCustomDesign.rimTopText = ''
  state.selectedCustomDesign.rimBottomText = ''
  state.activeConfig.containerRimBackgroundColor =
    state.defaultActiveConfig.containerRimBackgroundColor
  state.activeConfig.containerRimWidth = state.defaultActiveConfig.containerRimWidth

  const dataPatternColor = state.activeConfig.gridModuleColor
  const backgroundColor =
    state.activeConfig.containerBackgroundColor ?? state.activeConfig.gridBackgroundColor

  if (!dataPatternColor || !backgroundColor) return

  state.activeConfig.positionElementBottomLeft = colorPositionElement(
    state.activeConfig.positionElementBottomLeft,
    dataPatternColor,
    backgroundColor
  )
  state.activeConfig.positionElementTopLeft = colorPositionElement(
    state.activeConfig.positionElementTopLeft,
    dataPatternColor,
    backgroundColor
  )
  state.activeConfig.positionElementTopRight = colorPositionElement(
    state.activeConfig.positionElementTopRight,
    dataPatternColor,
    backgroundColor
  )

  const privacyText = state.activeConfig.containerText?.[0]
  if (privacyText?.fontColor && state.activeConfig.containerText) {
    state.activeConfig.containerText = [
      { ...privacyText, fontColor: dataPatternColor },
      ...state.activeConfig.containerText.slice(1)
    ]
  }
}

const revertVideoOptimizedStyles = (state: WritableDraft<CreateFlowcodeState>) => {
  const selectedDataPattern = state.data.templates.customizableTemplateOptions.find(
    template => template.id === state.selectedCustomDesign.pattern
  )
  const defaultPatternData = selectedDataPattern?.options ?? { ...state.defaultActiveConfig }
  state.activeConfig.positionElementBottomLeft = defaultPatternData.positionElementBottomLeft
  state.activeConfig.positionElementTopLeft = defaultPatternData.positionElementTopLeft
  state.activeConfig.positionElementTopRight = defaultPatternData.positionElementTopRight
  state.activeConfig.isTVCode = defaultPatternData.isTVCode
  state.activeConfig.containerBackgroundInsetExtend =
    defaultPatternData.containerBackgroundInsetExtend
  state.activeConfig.qrdensity = defaultPatternData.qrdensity
  state.activeConfig.containerBorderSize = defaultPatternData.containerBorderSize
  state.activeConfig.containerBorderInsetEyes = defaultPatternData.containerBorderInsetEyes
  state.activeConfig.dataGridPercentageSize = defaultPatternData.dataGridPercentageSize
  state.activeConfig.radiusMask = defaultPatternData.radiusMask
  state.activeConfig.containerRimPatternSize = defaultPatternData.containerRimPatternSize
  state.activeConfig.containerText = defaultPatternData.containerText
  state.activeConfig.gridModuleRandomSizes = defaultPatternData.gridModuleRandomSizes
  state.activeConfig.containerOuterPatternRandomSizes =
    defaultPatternData.containerOuterPatternRandomSizes
}

const applyEyeBranding = (state: WritableDraft<CreateFlowcodeState>) => {
  if (state.isUsingFlowcodeBranding) {
    addEyeBrandingToConfig(state.activeConfig)
  } else {
    removeEyeBrandingFromConfig(state.activeConfig)
  }
}

const applySinclairPrivacyText = (state: WritableDraft<CreateFlowcodeState>) => {
  if (!state.specialConfig.isSinclairOrgUser) return

  state.activeConfig.containerText = [
    {
      font: 'inter_medium',
      text: SINCLIAR_ORG_CODE_PRIVACY_TEXT,
      fontSize: 9,
      position: {
        property: 7,
        offsetPercentage: 3.1,
        extraDegrees: 0
      },
      fontColor: state.activeConfig.gridModuleColor ?? '#000000'
    }
  ]
}

const designChangeEffects = (state: WritableDraft<CreateFlowcodeState>) => {
  if (state.designType === DesignType.CUSTOM && state.selectedCustomDesign.shape === 'circle') {
    if (state.isUsingVideoOptimization) {
      writeVideoOptimizedStyles(state)
    } else {
      revertVideoOptimizedStyles(state)
    }
  }

  // must be after video optimization
  applyEyeBranding(state)

  if (state.specialConfig.hidePrivacySmile) {
    state.activeConfig.containerText = []
  }

  applySinclairPrivacyText(state)

  state.didScannabilityCheckPass = null
}

const deleteEmptyObjectValuesOnAssignment = (
  obj: Record<string, unknown>
): Record<string, unknown> => {
  return Object.keys(obj).reduce(
    (r: Record<string, unknown>, key) => (obj[key] && (r[key] = obj[key]), r),
    {}
  )
}

const createFlowcode = createSlice({
  name: 'createFlowcode',
  initialState,
  reducers: {
    setTemplateData: (state, action) => {
      state.data.templates = action.payload
    },
    setDefaultActiveConfig: (state, action) => {
      const template = action.payload
      state.defaultActiveConfig = { ...template.options }
      state.defaultCustomDesign = { ...placeholderCustomDesign, pattern: template.id }
      state.selectedCustomDesign = state.defaultCustomDesign
    },
    setAutoDesignCodeData: (state, action) => {
      state.data.autoDesignedCodes = action.payload

      if (state.data.firstAutoDesignedCodes.length === 0) {
        state.data.firstAutoDesignedCodes = action.payload
      }
    },
    setOrgData: (state, action) => {
      state.data.org = action.payload
    },
    setDirectoryId: (state, action) => {
      state.directoryId = action.payload
    },
    setRedirectTo: (state, action) => {
      state.redirectTo = action.payload
    },
    setActiveConfig: (state, action) => {
      state.activeConfig = action.payload
    },
    // page control
    setIsCreateOneFlowcode: (state, action) => {
      state.isCreateOneFlowcode = action.payload
    },
    setIsSharingWithAllMembers: (state, action) => {
      state.isSharingWithAllMembers = action.payload
    },
    setIsScannabilityModalOpen: (state, action) => {
      state.isScannabilityModalOpen = action.payload
    },
    setDidScannabilityCheckPass: (state, action) => {
      state.didScannabilityCheckPass = action.payload
    },
    setIsAttemptingToSubmit: (state, action) => {
      state.isAttemptingToSubmit = action.payload
    },
    setPostCreateRedirectTo: (state, action) => {
      state.postCreateRedirectTo = action.payload
    },
    setHasDestinationError: (state, action) => {
      state.hasDestinationError = action.payload
    },
    setHasVCardError: (state, action) => {
      const next = { ...state.hasVCardError, ...action.payload }

      state.hasVCardError = deleteEmptyObjectValuesOnAssignment(next)
    },
    setIsUserAtCodeLimit: (state, action) => {
      state.isUserAtCodeLimit = action.payload
    },
    resetState: () => initialState,
    setSelectedSavedDesignName: (state, action) => {
      state.selectedSavedDesignName = action.payload
    },
    // single create
    setFlowcodeNickname: (state, action) => {
      state.flowcodeNickname = action.payload
    },
    // bulk create
    setCSVUrl: (state, action) => {
      state.csvUrl = action.payload
    },
    setFolderName: (state, action) => {
      state.folderName = action.payload
    },
    // destination
    setDestinationType: (state, action) => {
      state.destinationValue = initialState.destinationValue
      state.destinationType = action.payload
    },
    setDestinationValue: (state, action) => {
      if (state.destinationType === DestinationType.VCARD) {
        const next = { ...state.destinationValue.vcard, ...action.payload.vcard }
        state.destinationValue.vcard = deleteEmptyObjectValuesOnAssignment(next)
      } else if (
        state.destinationType === DestinationType.SOCIALS &&
        state.destinationValue.platform === Social.Venmo
      ) {
        state.destinationValue.venmo = { ...state.destinationValue.venmo, ...action.payload.venmo }
      } else {
        state.destinationValue = { ...state.destinationValue, ...action.payload }
      }
    },
    // design
    setDesignType: (state, action) => {
      state.data.autoDesignedCodes = state.data.firstAutoDesignedCodes
      state.selectedAutoDesign = { ...defaultAutoDesign }
      state.selectedCustomDesign = { ...state.defaultCustomDesign }
      state.selectedSavedDesignFlowcodeTemplate = ''
      state.selectedSavedDesignBatchTemplate = ''

      state.activeConfig = { ...state.defaultActiveConfig }
      state.isUsingVideoOptimization = false
      state.isUsingFlowcodeBranding = true

      state.selectedSavedDesignName = ''

      state.designType = action.payload

      designChangeEffects(state)
    },
    setSelectedAutoDesign: (state, action) => {
      state.selectedAutoDesign = { ...state.selectedAutoDesign, ...action.payload }
      designChangeEffects(state)
    },
    setSelectedCustomDesign: (state, action) => {
      state.selectedCustomDesign = { ...state.selectedCustomDesign, ...action.payload }
      const {
        pattern,
        singleColor,
        shape,
        centerText,
        rimTopText,
        rimBottomText,
        logoData,
        video,
        showFrame
      } = state.selectedCustomDesign

      const willBeSquareShaped = shape === 'square'

      const selectedDataPattern = state.data.templates.customizableTemplateOptions.find(
        template => template.id === pattern
      )

      // every active config starts fresh from pattern selection
      state.activeConfig = selectedDataPattern?.options ?? { ...state.defaultActiveConfig }

      if (willBeSquareShaped) {
        state.isUsingVideoOptimization = false
        state.selectedCustomDesign.video = false

        state.selectedCustomDesign.rimTopText = rimTopText.slice(0, 20)
        state.selectedCustomDesign.rimBottomText = rimBottomText.slice(0, 20)

        const { name } = selectedDataPattern ?? {}

        //the square code pattern's asset frame will be replaced by the container rim
        if (name && !showFrame) {
          const key = name
            .slice(name.indexOf('/') + 1, name.length)
            .trim()
            .toLowerCase()

          const nextConfig = getSquareShapeOptions(key, state.specialConfig.isSinclairOrgUser)

          state.activeConfig = { ...state.activeConfig, ...nextConfig }
        }
      } else {
        const isVideoOptimized = showFrame ? false : video
        state.isUsingVideoOptimization = isVideoOptimized
        state.selectedCustomDesign.video = isVideoOptimized

        state.selectedCustomDesign.rimTopText = rimTopText.slice(0, 19)
        state.selectedCustomDesign.rimBottomText = rimBottomText.slice(0, 18)
      }

      const isScannable =
        isContrastRatioScannable(singleColor) || isPredefinedColorChipColor(singleColor)

      const backgroundColor = isScannable ? '#FFFFFF' : singleColor
      const dataPatternColor = isScannable ? singleColor : '#000000'

      state.activeConfig.containerBackgroundColor = backgroundColor
      state.activeConfig.gridBackgroundColor = backgroundColor
      state.activeConfig.gridModuleColor = dataPatternColor
      state.activeConfig.containerOuterPatternColor = dataPatternColor

      state.activeConfig.positionElementBottomLeft = colorPositionElement(
        state.activeConfig.positionElementBottomLeft,
        dataPatternColor,
        backgroundColor
      )
      state.activeConfig.positionElementTopLeft = colorPositionElement(
        state.activeConfig.positionElementTopLeft,
        dataPatternColor,
        backgroundColor
      )
      state.activeConfig.positionElementTopRight = colorPositionElement(
        state.activeConfig.positionElementTopRight,
        dataPatternColor,
        backgroundColor
      )

      const privacyText = state.activeConfig.containerText?.[0]
      if (privacyText?.fontColor) {
        privacyText.fontColor = dataPatternColor
      }

      if (centerText.length > 0) {
        state.activeConfig.centerText = [
          {
            text: centerText,
            font: 'inter_bold',
            fontColor: dataPatternColor,
            position: {
              property: 0,
              offsetPercentage: 0,
              extraDegrees: 0
            },
            // the starting fontSize for autoscaling matters
            // I wonder why the new autoscaled font size doesn't impact subsequent autoscaling
            fontSize: 12
          }
        ]
        state.activeConfig.centerTextPadding = 4
      } else {
        state.activeConfig.centerText = state.defaultActiveConfig.centerText
        state.activeConfig.centerTextPadding = state.defaultActiveConfig.centerTextPadding
      }

      let rimText = [] as FcText[]

      const rimFont = showFrame ? 'centra_light' : 'inter_bold'
      const rimFontColor = showFrame ? '#FFFFFF' : dataPatternColor
      const frameFontSize = willBeSquareShaped
        ? DEFAULT_LOCG_SQUARE_RIM_TEXT_FONTSIZE
        : DEFAULT_LOCG_CIRCLE_RIM_TEXT_FONTSIZE
      const rimFontSize = showFrame ? frameFontSize : 26

      const createRimText = (text: string, property: 6 | 7): FcText => ({
        text,
        font: rimFont,
        fontColor: rimFontColor,
        position: {
          property,
          offsetPercentage: 0,
          extraDegrees: 0
        },
        fontSize: rimFontSize
      })

      if (rimTopText.length > 0) rimText.push(createRimText(rimTopText, 6))
      if (rimBottomText.length > 0) rimText.push(createRimText(rimBottomText, 7))

      if (rimText.length > 0) {
        state.activeConfig.containerRimText = rimText
        state.activeConfig.containerRimWidth = 26
      } else {
        state.activeConfig.containerRimText = state.defaultActiveConfig.containerRimText
        state.activeConfig.containerRimWidth = state.defaultActiveConfig.containerRimWidth
      }

      if (logoData.logoImageUrl.length > 0) {
        const { logoImageUrl, logoHeight, logoWidth } = logoData
        state.activeConfig.logoImageUrl = logoImageUrl
        state.activeConfig.logoHeight = logoHeight
        state.activeConfig.logoWidth = logoWidth
        state.activeConfig.logoOuterPadding = 2
        state.activeConfig.errorCorrectionLevel = FcErrorCorrectionLevel.H
      } else {
        state.activeConfig.logoImageUrl = state.defaultActiveConfig.logoImageUrl
        state.activeConfig.logoHeight = state.defaultActiveConfig.logoHeight
        state.activeConfig.logoWidth = state.defaultActiveConfig.logoWidth
        state.activeConfig.logoOuterPadding = state.defaultActiveConfig.logoOuterPadding
        state.activeConfig.errorCorrectionLevel = state.defaultActiveConfig.errorCorrectionLevel
      }

      if (showFrame) {
        const selectedShape = willBeSquareShaped ? FcShape.SQUARE : FcShape.CIRCLE

        state.activeConfig = {
          ...state.activeConfig,
          containerBackgroundColor: backgroundColor,
          gridBackgroundColor: backgroundColor,
          ...getFrameOptions({
            showFrame,
            selectedShape,
            dataPatternColor,
            backgroundColor
          }),
          containerShape: selectedShape,
          containerRimBackgroundColor: dataPatternColor,
          containerText: createFrameContainerText(
            selectedShape,
            true,
            dataPatternColor,
            state.specialConfig.isSinclairOrgUser
          )
        }
        applyEyeBranding(state)
      } else {
        designChangeEffects(state)
      }

      if (!showFrame) {
        state.activeConfig.containerBorderColor = dataPatternColor
      }
    },
    setSelectedSavedDesignFlowcodeTemplate: (state, action) => {
      state.selectedSavedDesignBatchTemplate = ''

      const {
        id: d,
        name,
        options
      }: { id: string; name: string; options: FcGeneratorOptions } = action.payload
      const id = options?.id ?? d
      state.selectedSavedDesignFlowcodeTemplate = id
      state.activeConfig = { ...options }
      state.selectedSavedDesignName = name

      const doesTemplateAlreadyHaveFlowcodeBranding = [
        options?.positionElementTopRight?.outerShape,
        options?.positionElementBottomLeft?.outerShape
      ].includes(FcShape.LOGO)
      const doesTemplateAlreadyHaveVideoOptimization = Boolean(options.isTVCode)

      state.isUsingVideoOptimization = doesTemplateAlreadyHaveVideoOptimization
      state.isUsingFlowcodeBranding = doesTemplateAlreadyHaveFlowcodeBranding

      designChangeEffects(state)
    },
    setSelectedSavedDesignBatchTemplate: (state, action) => {
      state.selectedSavedDesignFlowcodeTemplate = ''

      const { id: d, name, options } = action.payload
      const id = options?.id ?? d
      state.selectedSavedDesignBatchTemplate = id
      state.activeConfig = { ...options }
      state.selectedSavedDesignName = name

      const doesTemplateAlreadyHaveFlowcodeBranding = [
        options?.positionElementTopRight?.outerShape,
        options?.positionElementBottomLeft?.outerShape
      ].includes(FcShape.LOGO)
      const doesTemplateAlreadyHaveVideoOptimization = Boolean(options.isTVCode)

      state.isUsingVideoOptimization = doesTemplateAlreadyHaveVideoOptimization
      state.isUsingFlowcodeBranding = doesTemplateAlreadyHaveFlowcodeBranding

      designChangeEffects(state)
    },
    setSpecialConfig: (state, action) => {
      state.specialConfig = { ...state.specialConfig, ...action.payload }
      designChangeEffects(state)
    },
    // else
    setSaveDesignAsTemplate: (state, action) => {
      state.saveDesignAsTemplate = action.payload
    },
    setIsUsingStaticFlowcode: (state, action) => {
      state.isUsingStaticFlowcode = action.payload
    },
    setSelectedRedirectSubdomain: (state, action) => {
      state.selectedRedirectSubdomain = action.payload
    },
    setIsUsingFlowcodeBranding: (state, action) => {
      state.isUsingFlowcodeBranding = action.payload

      if (action.payload) {
        addEyeBrandingToConfig(state.activeConfig)
      } else {
        removeEyeBrandingFromConfig(state.activeConfig)
      }
    },
    setIsUsingAdvancedGeolocation: (state, action) => {
      state.isUsingAdvancedGeolocation = action.payload
    },
    setSelectedTags: (state, action) => {
      state.selectedTags = action.payload
    },
    setSelectedMembers: (state, action) => {
      state.selectedMembers = action.payload
    }
  }
})

export const {
  setTemplateData,
  setAutoDesignCodeData,
  setOrgData,
  setActiveConfig,
  setDirectoryId,
  setRedirectTo,
  setDefaultActiveConfig,
  // page control
  setIsCreateOneFlowcode,
  setIsSharingWithAllMembers,
  setIsScannabilityModalOpen,
  setDidScannabilityCheckPass,
  setIsAttemptingToSubmit,
  setPostCreateRedirectTo,
  setHasDestinationError,
  setHasVCardError,
  setIsUserAtCodeLimit,
  resetState,
  setSelectedSavedDesignName,
  // single create
  setFlowcodeNickname,
  setDestinationType,
  setDestinationValue,
  // bulk create
  setCSVUrl,
  setFolderName,
  // design
  setDesignType,
  setSelectedAutoDesign,
  setSelectedCustomDesign,
  setSelectedSavedDesignFlowcodeTemplate,
  setSelectedSavedDesignBatchTemplate,
  setSpecialConfig,
  setIsUsingFlowcodeBranding,
  // else
  setSaveDesignAsTemplate,
  setIsUsingStaticFlowcode,
  setSelectedRedirectSubdomain,
  setIsUsingAdvancedGeolocation,
  setSelectedTags,
  setSelectedMembers
} = createFlowcode.actions

export default createFlowcode.reducer
